import { createContext, type ReactNode, useContext } from "react";
import { useTranslation } from "react-i18next";
import { useXmlConfiguratorTranslations } from "~/features/configurator/common/useXmlConfiguratorTranslations";
import { Card } from "~/features/configurator/Linear/components/Card";
import { type LinearTileProps } from "~/features/configurator/Linear/components/LinearTile";
import LanguageAwareExternalContent from "~/features/configurator/Linear/components/LanguageAwareExternalContent";
import LinearInfo from "~/features/configurator/Linear/components/LinearInfo";
import { CheckedCircle } from "~/common/components/CheckedCircle/CheckedCircle";
import { styled } from "~/common/mui";
import StarIcon from "@mui/icons-material/Star";

const checkedIndicatorClass = "linear-tile-v2-checked-indicator";

const TileCardFrame = styled("div")(({ theme }) => ({
  width: "100%",
  height: "100%",
  display: "flex",
  flexFlow: "column",
  "& > .scha__lc__card": {
    background: "unset",
    border: "none",
    width: "100%",
    height: "100%",
    display: "flex",
    flexFlow: "column",
    padding: theme.spacing(2),
    gap: theme.spacing(1),
    color: theme.palette.text.primary,
    [`&:hover .${checkedIndicatorClass}`]: {
      visibility: "visible",
    },
  },
}));

const StandardIndicatorRoot = styled("div")(({ theme }) => ({
  display: "flex",
  flexFlow: "row",
  alignItems: "center",
  gap: theme.spacing(1),
  "& > i": {
    color: theme.palette.primary.main,
  },
}));

const ImageEl = styled("img")({
  background: "gray",
});

const CheckedIndicatorRoot = styled(CheckedCircle)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  right: theme.spacing(2),
  pointerEvents: "none",
  visibility: "hidden",
}));

const HeadlineRoot = styled("p")(({ theme }) => ({
  ...theme.typography.titleMedium,
  margin: 0,
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1),
}));

const SubHeadlineRoot = styled("p")(({ theme }) => ({
  ...theme.typography.labelLarge,
  margin: 0,
  display: "flex",
  alignItems: "flex-start",
  gap: theme.spacing(1),
}));


type Props = Omit<LinearTileProps, "onClickHandler" | "isPicked" | "isDisabled" | "link"> & {
  onClick?: () => void;
  selected?: boolean;
  disabled?: boolean;
  href?: string;
  target?: JSX.IntrinsicElements["a"]["target"];
  style?: JSX.IntrinsicElements["div"]["style"];
};
export const LinearTileV2 = (props: Props) => {
  return <Base {...props} />;
};

const Base = ({
  selected,
  disabled,
  className,
  href,
  style,
  ...rest
}: Pick<
  Props,
  "onClick" | "selected" | "disabled" | "className" | "href" | "target" | "children" | "style" | "error"
>) => {
  return (
    <IsPickedContext.Provider value={selected}>
      <TileCardFrame className={className} style={style}>
        <Card
          disabled={disabled}
          active={selected}
          disableHover={disabled || (!rest.onClick && !href)}
          as={href ? "a" : "button"}
          href={href}
          {...rest}
        />
      </TileCardFrame>
    </IsPickedContext.Provider>
  );
};
LinearTileV2.Base = Base;

const IsPickedContext = createContext<boolean | undefined>(false);

const StandardIndicator = ({
  className,
  omitText,
  ...props
}: Omit<JSX.IntrinsicElements["div"], "children"> & { omitText?: boolean }) => {
  const { t } = useTranslation();
  return (
    <StandardIndicatorRoot className={className} {...props}>
      <StarIcon  />
      {!omitText && <span>{t("configurator.standard")}</span>}
    </StandardIndicatorRoot>
  );
};
LinearTileV2.StandardIndicator = StandardIndicator;

const Image = ({ tKey = "", ratio, src }: { tKey?: string; src?: string; ratio: string }) => {
  const { t } = useXmlConfiguratorTranslations();
  const imgSrc = src ?? t?.(tKey) ?? tKey;
  return <ImageEl style={{ aspectRatio: ratio }} src={imgSrc} alt="" />;
};
LinearTileV2.Image = Image;

const CheckedIndicator = () => {
  const isPicked = useContext(IsPickedContext);
  return <CheckedIndicatorRoot className={checkedIndicatorClass} checked={isPicked} />;
};
LinearTileV2.CheckedIndicator = CheckedIndicator;

function renderContent(content: ReactNode) {
  return typeof content === "string" ? (
    <LanguageAwareExternalContent content={content} className={"scha__lc__external-label"} />
  ) : (
    content
  );
}

const HeadlineContent = ({ content, children }: { content: ReactNode; children?: ReactNode }) => (
  <HeadlineRoot>
    {renderContent(content)}
    {children}
  </HeadlineRoot>
);
LinearTileV2.Headline = HeadlineContent;

const SubHeadlineContent = ({ content, children }: { content: ReactNode; children?: ReactNode }) => (
  <SubHeadlineRoot>
    {renderContent(content)}
    {children}
  </SubHeadlineRoot>
);
LinearTileV2.SubHeadline = SubHeadlineContent;
LinearTileV2.Info = LinearInfo;
